'use client';

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Download, ExternalLink, RotateCw, X, ZoomIn, ZoomOut } from 'lucide-react';

export interface ReviewImageAsset {
  url: string;
  alt?: string;
  caption?: string | null;
}

interface ReviewImageLightboxProps {
  isOpen: boolean;
  images: ReviewImageAsset[];
  initialIndex?: number;
  onClose: () => void;
  title?: string;
}

export default function ReviewImageLightbox({
  isOpen,
  images,
  initialIndex = 0,
  onClose,
  title,
}: ReviewImageLightboxProps) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setActiveIndex(Math.min(Math.max(initialIndex, 0), Math.max(images.length - 1, 0)));
    setZoom(1);
    setRotation(0);
  }, [images.length, initialIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (images.length <= 1) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setActiveIndex((current) => (current === 0 ? images.length - 1 : current - 1));
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setActiveIndex((current) => (current === images.length - 1 ? 0 : current + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [images.length, isOpen, onClose]);

  const activeImage = useMemo(() => images[activeIndex] || null, [activeIndex, images]);

  if (!mounted || !isOpen || !activeImage) {
    return null;
  }

  const canNavigate = images.length > 1;
  const showPrevious = () => setActiveIndex((current) => (current === 0 ? images.length - 1 : current - 1));
  const showNext = () => setActiveIndex((current) => (current === images.length - 1 ? 0 : current + 1));

  return createPortal(
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/95 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Image viewer'}
    >
      <div
        className="relative flex h-full w-full max-w-7xl flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b border-white/10 px-2 py-3 text-white sm:px-4">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold sm:text-base">{title || activeImage.alt || 'Source document'}</p>
            <p className="text-xs text-white/60 sm:text-sm">
              {images.length > 1 ? `Image ${activeIndex + 1} of ${images.length}` : 'Image preview'}
            </p>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => setZoom((current) => Math.max(0.5, current - 0.25))}
              className="rounded-lg p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
              title="Zoom out"
            >
              <ZoomOut className="h-5 w-5" />
            </button>
            <span className="min-w-[52px] text-center text-xs text-white/70 sm:text-sm">{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              onClick={() => setZoom((current) => Math.min(4, current + 0.25))}
              className="rounded-lg p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
              title="Zoom in"
            >
              <ZoomIn className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setRotation((current) => (current + 90) % 360)}
              className="rounded-lg p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
              title="Rotate"
            >
              <RotateCw className="h-5 w-5" />
            </button>
            <a
              href={activeImage.url}
              download
              className="rounded-lg p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
              title="Download image"
            >
              <Download className="h-5 w-5" />
            </a>
            <button
              type="button"
              onClick={() => window.open(activeImage.url, '_blank', 'noopener,noreferrer')}
              className="rounded-lg p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
              title="Open in new tab"
            >
              <ExternalLink className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-red-600/90 p-2 text-white transition hover:bg-red-600"
              title="Close preview"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="relative flex flex-1 items-center justify-center overflow-auto px-2 py-4 sm:px-6">
          {canNavigate && (
            <>
              <button
                type="button"
                onClick={showPrevious}
                className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white transition hover:bg-black/70 sm:left-4"
                title="Previous image"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                onClick={showNext}
                className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white transition hover:bg-black/70 sm:right-4"
                title="Next image"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          <img
            src={activeImage.url}
            alt={activeImage.alt || title || 'Source document'}
            className="max-h-full max-w-full rounded-xl object-contain shadow-2xl"
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
              transition: 'transform 0.2s ease',
            }}
          />
        </div>

        {(activeImage.caption || activeImage.alt) && (
          <div className="border-t border-white/10 px-4 py-3 text-sm text-white/75">
            {activeImage.caption || activeImage.alt}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
